import * as React from "react";
import { PersonRegular } from "@fluentui/react-icons";
import {
  Button,
  Checkbox,
  Input,
  Spinner,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { searchGroups, fetchTransitiveMembers } from "../services/groupService";

const useStyles = makeStyles({
  root: {
    height: "100vh",
    padding: "10px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    backgroundColor: tokens.colorNeutralBackground1,
    boxSizing: "border-box",
    overflow: "hidden",
    
  },
  title: {
    margin: 0,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    flexShrink: 0,
  },
  searchBar: {
    display: "flex",
    gap: "8px",
    flexShrink: 0,
  },

  
  resultList: {
    display: "flex",
    flexDirection: "row",
    gap: "6px",
    overflow: "hidden",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    padding: "6px",
    borderRadius: "4px",
    flex: "1 1 0",
    minHeight: 0,
  },
  resultListLeft: {
    width: "70%",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    overflowY: "auto",
    minHeight: 0,
  },
  resultListRight: {
    width: "30%",
    display: "flex",
    flexDirection: "column",
    borderLeft: `1px solid ${tokens.colorNeutralStroke1}`,
    paddingLeft: "12px",
    overflowY: "auto",
    minHeight: 0,
  },
  resultItem: {
    display: "flex",
    alignItems: "center",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    gap: "8px",
    paddingBottom: "8px",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    flexShrink: 0,
  },
  errorMessage: {
    flexShrink: 0,
  },
  loadingContainer: {
    flexShrink: 0,
  },
  loadingMembers: {
    flexShrink: 0,
  },
  resultMeta: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  resultClickable: {
    background: "none",
    border: "none",
    padding: "8px",
    textAlign: "left",
    cursor: "pointer",
    borderRadius: "4px",
    width: "100%",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground2,
    },
    "&:active": {
      backgroundColor: tokens.colorNeutralBackground4,

    },
    "&:focus": {
      backgroundColor: tokens.colorNeutralBackground4,
      borderLeft: `3px solid #0078D4`,
    },
  },
  resultClickableSelected: {
    backgroundColor: tokens.colorNeutralBackground4,
    borderLeft: `3px solid #0078D4`,
  },
  memberMeta: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    fontWeight: "bold",
  },
});

const SearchDialog = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const params = React.useMemo(() => new URLSearchParams(window.location.search), []);
  const initialQuery = params.get("q") || "";
  // Dialog 內通常拿不到 mailbox.item，因此由 Parent 透過 messageChild 傳入 isCalendarMode
  const [isCalendarMode, setIsCalendarMode] = React.useState(false);

  const [query, setQuery] = React.useState(initialQuery);
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [loadingMembers, setLoadingMembers] = React.useState(false);
  const [selectedItems, setSelectedItems] = React.useState(new Set());
  const [clickedItemId, setClickedItemId] = React.useState(null);
  const [accessToken, setAccessToken] = React.useState(null);
  const [tokenError, setTokenError] = React.useState(null);

  const [groupMembers, setGroupMembers] = React.useState([]);

  React.useEffect(() => {
    Office.onReady().then(() => {
      const handleParentMessage = (args) => {
        try {
          const payload = JSON.parse(args.message);
          if (payload.type === "token" && payload.token) {
            setAccessToken(payload.token);
            setTokenError(null);
            return;
          }
          if (payload.type === "context" && typeof payload.isCalendarMode === "boolean") {
            setIsCalendarMode(payload.isCalendarMode);
            return;
          }
        } catch (error) {
          console.error("Failed to parse parent message:", error);
        }
      };

      Office.context.ui.addHandlerAsync(
        Office.EventType.DialogParentMessageReceived,
        handleParentMessage
      );

      Office.context.ui.messageParent(JSON.stringify({ type: "requestToken" }));
    });
  }, []);

  const performSearch = React.useCallback(
    async (searchText) => {
      if (!searchText || !accessToken) {
        if (!accessToken) {
          setTokenError("Waiting for access token from parent...");
        }
        return;
      }
      setLoading(true);
      setResults([]);
      setGroupMembers([]);
      setSelectedItems(new Set());
      try {
        // 使用服務層進行搜尋
        const getAccessToken = async () => accessToken;
        const tokenRef = { current: accessToken };
        const data = await searchGroups(searchText, getAccessToken, tokenRef);
        setResults(data);
      } catch (error) {
        console.error("Dialog search error:", error);
      } finally {
        setLoading(false);
      }
    },
    [accessToken]
  );

  React.useEffect(() => {
    if (initialQuery && accessToken) {
      performSearch(initialQuery);
    }
  }, [initialQuery, accessToken, performSearch]);

  const toggleSelection = (item) => {
    const next = new Set(selectedItems);
    if (next.has(item.id)) {
      next.delete(item.id);
    } else {
      next.add(item.id);
    }
    setSelectedItems(next);
  };

  // 獲取選中群組的所有成員（包括子部門）
  const handleFetchTransitiveMembers = React.useCallback(async (groupId) => {
    setGroupMembers([]);
    if (!groupId || !accessToken) {
        return;
    }
    setLoadingMembers(true);
    try {
        const getAccessToken = async () => accessToken;
        const tokenRef = { current: accessToken };
        const data = await fetchTransitiveMembers(groupId, getAccessToken, tokenRef);
        
        // 排序成員
        const sortedMembers = data.sort((a, b) => {
            const aName = a.givenName?.replace(/^[A-Za-z]+/, '') || '';
            const bName = b.givenName?.replace(/^[A-Za-z]+/, '') || '';
            return aName - bName;
        });
        setGroupMembers(sortedMembers);
    } catch (error) {
        console.error("Error fetching transitive members:", error);
        setGroupMembers([]);
    } finally {
        setLoadingMembers(false);
    }
  }, [accessToken]);

  const closeDialog = () => {
    if (Office.context?.ui?.closeContainer) {
      Office.context.ui.closeContainer();
    }
  };

  const sendMessage = (payload) => {
    try {
      Office.context.ui.messageParent(JSON.stringify(payload));
    } catch (error) {
      console.error("Failed to message parent:", error);
    }
  };

  // messageType:
  // - Mail: "to" | "cc" | "bcc"
  // - Calendar: "requiredAttendees" | "optionalAttendees"
  const handleConfirm = (messageType) => {
    const selected = results.filter((item) => selectedItems.has(item.id));
    sendMessage({
      type: "searchResults",
      recipients: selected,
      messageType: messageType,
    });
    closeDialog();
  };

  const handleClear = () => {
    setSelectedItems(new Set());
  };

  const handleCancel = () => {
    sendMessage({ type: "cancel" });
    closeDialog();
  };

  return (
    <div className={styles.root}>
      <h2 className={styles.title}>{t('search_label')}</h2>
      <div className={styles.searchBar}>
        <Input
          placeholder={t('search_placeholder')}
          value={query}
          onChange={(e, data) => setQuery(data.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              performSearch(query);
            }
          }}
        />
        <Button appearance="primary" onClick={() => performSearch(query)}>
          {t('search')}
        </Button>
        {groupMembers.length > 0 && <span style={{flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>{groupMembers.length} {t('member_count') || '位成員'}</span>}
      </div>
      {tokenError && (
        <div className={styles.errorMessage} style={{ color: tokens.colorPaletteRedForeground2 }}>{tokenError}</div>
      )}

      {loading ? (
        <div className={styles.loadingContainer}>
          <Spinner label={t('loading')} />
        </div>
      ) : (
        <div className={styles.resultList}>
          <div className={styles.resultListLeft}>
            {results.length === 0 && <div>{t('no_results')}</div>}
            {results.map((item) => (
              <div className={styles.resultItem} key={item.id}>
                <Checkbox
                  checked={selectedItems.has(item.id)}
                  onChange={() => toggleSelection(item)}
                  // 讓Checkbox點擊只影響自己
                  onClick={e => e.stopPropagation()}
                />
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <button
                    type="button"
                    className={`${styles.resultClickable} ${clickedItemId === item.id ? styles.resultClickableSelected : ''}`}
                    onClick={e => {
                      e.stopPropagation(); 
                      setClickedItemId(item.id); // 設定點擊的項目 ID
                      if(item.groupTypes) {
                        handleFetchTransitiveMembers(item.id);
                      }
                    }}
                    tabIndex={0}
                  >
                    {item.displayName}
                    <br />
                    <span className={styles.resultMeta}>{item.mail || item.userPrincipalName}</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.resultListRight}>
          {loadingMembers && <Spinner label={t('loading_members')} />}
            {groupMembers.map((member) => (
              <div className={styles.resultItem} key={member.id}>
                <PersonRegular className={styles.resultIcon} />
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span className={styles.memberMeta}>{`${(member.givenName ? member.givenName.toUpperCase() : " ")} ${member.displayName? member.displayName : " "}`}</span>
                  <span className={styles.resultMeta}>{member.mail || ""}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.actions}>
        {isCalendarMode ? (
          <>
            <Button
              appearance="primary"
              onClick={() => handleConfirm("requiredAttendees")}
              disabled={selectedItems.size === 0}
            >
              {t('add_required_attendees')}
            </Button>
            <Button
              appearance="primary"
              onClick={() => handleConfirm("optionalAttendees")}
              disabled={selectedItems.size === 0}
            >
              {t('add_optional_attendees')}
            </Button>
          </>
        ) : (
          <>
            <Button
              appearance="primary"
              onClick={() => handleConfirm("to")}
              disabled={selectedItems.size === 0}
            >
              {t('add_recipient')}
            </Button>
            <Button
              appearance="primary"
              onClick={() => handleConfirm("cc")}
              disabled={selectedItems.size === 0}
            >
              {t('add_cc')}
            </Button>
            <Button
              appearance="primary"
              onClick={() => handleConfirm("bcc")}
              disabled={selectedItems.size === 0}
            >
              {t('add_bcc')}
            </Button>
          </>
        )}
        <Button
          appearance="secondary"
          onClick={handleClear}
          disabled={selectedItems.size === 0}
          style={{ color: tokens.colorPaletteRedForeground2 }}
        >
          {t('clear_selection')}
        </Button>
      </div>
      <div className={styles.actions}>

      </div>
    </div>
  );
};

export default SearchDialog;

